// What a browser session *is*, in the terms an operator thinks in.
//
// The console used to name a session by the file it was loaded from
// (`/var/veil/install/profiles/150/latest/android/android_13.0.0_mali-g57_
// m2103k19c_2947ec.enc`) or by a bare uuid. Neither answers the only question
// a confirmation dialog exists to ask, which is "is this the one I meant".
//
// The worker already resolves every bundle to a fingerprint and hands it back
// on `/browsers/:id/info` and `GET /sessions/:id`, so nothing here is invented:
// this module only formats what the worker reports. The filename parser is the
// fallback for the one case where a path is all we have, and it mirrors
// `parseNewFilename` in local-api's gpu-parser, which is what produced the name.

/** The resolved fingerprint, as the worker reports it. */
export interface SessionFingerprint {
	os?: string | null;
	osVersion?: string | null;
	/** Present for Windows profiles: the marketing version, "10" or "11". */
	windowsVersion?: string | null;
	/** Android device model, e.g. "M2103K19C". */
	model?: string | null;
	/** Human-cased GPU string, e.g. "Mali-G57". */
	unmaskedRenderer?: string | null;
	gpuName?: string | null;
	browserVersion?: number | null;
	arch?: string | null;
	/** Short content hash: the last field of the bundle filename. */
	hash?: string | null;
	filename?: string | null;
}

const OS_LABELS: Record<string, string> = {
	win: "Windows",
	windows: "Windows",
	mac: "macOS",
	macos: "macOS",
	linux: "Linux",
	android: "Android",
};

/**
 * Bundle filenames are `<os>_<osVersion>_<gpu>[_<model>]_<hash>.enc`, with the
 * model field present only for Android. Anchored so a path that does not match
 * the shape returns null instead of a plausible-looking wrong answer.
 */
const BUNDLE_NAME = /^([a-z]+)_([\d.]+)_([^_]+)(?:_([^_]+))?_([^_]+)$/i;

/** Trailing zero segments carry no information: "13.0.0" is Android 13. */
function trimVersion(version: string): string {
	const trimmed = version.replace(/(\.0)+$/, "");
	return trimmed.length > 0 ? trimmed : version;
}

/** "mali-g57" reads as a slug; "Mali-G57" reads as a GPU. */
function titleCaseGpu(slug: string): string {
	return slug
		.split("-")
		.map((part) =>
			/^\d/.test(part)
				? part.toUpperCase()
				: part.charAt(0).toUpperCase() + part.slice(1),
		)
		.join("-");
}

/**
 * Recover a fingerprint from a bundle path when that is all the caller has.
 *
 * Strictly a fallback. The worker's own answer is richer and, unlike a
 * filename, cannot go stale relative to what the browser actually loaded.
 */
export function fingerprintFromBundlePath(
	pathOrFilename: string | null | undefined,
): SessionFingerprint | null {
	if (!pathOrFilename) return null;
	const filename = pathOrFilename.split(/[/\\]/).pop() ?? "";
	const stem = filename.replace(/\.enc$/i, "");
	const match = BUNDLE_NAME.exec(stem);
	if (!match) return null;

	const [, osRaw, osVersion, gpuName, model, hash] = match;
	const os = osRaw?.toLowerCase() === "windows" ? "win" : (osRaw ?? "");
	return {
		os,
		osVersion: osVersion ?? null,
		model: model ?? null,
		gpuName: gpuName ?? null,
		hash: hash ?? null,
		filename,
	};
}

export interface SessionIdentity {
	/** "Android 13 · Mali-G57 · M2103K19C", or null when nothing is known. */
	label: string | null;
	os: string | null;
	device: string | null;
	renderer: string | null;
	hash: string | null;
}

/**
 * Compose the one line that tells an operator which browser this is.
 *
 * Every component is optional because a fingerprint can be partial, and a
 * partial answer beats a path. Returns a null label rather than a placeholder
 * so callers can decide what to show when there is genuinely nothing.
 */
export function describeSession(
	fingerprint: SessionFingerprint | null | undefined,
): SessionIdentity {
	if (!fingerprint) {
		return { label: null, os: null, device: null, renderer: null, hash: null };
	}

	const osKey = fingerprint.os?.toLowerCase() ?? "";
	const osName = OS_LABELS[osKey] ?? (fingerprint.os || null);

	// Windows reports a Client Hints platformVersion ("15.0.0") that is not the
	// version anybody calls it, so the worker's normalised value wins there.
	const version =
		osKey === "win" || osKey === "windows"
			? (fingerprint.windowsVersion ?? null)
			: fingerprint.osVersion
				? trimVersion(fingerprint.osVersion)
				: null;

	const os = osName ? (version ? `${osName} ${version}` : osName) : null;
	const renderer =
		fingerprint.unmaskedRenderer ||
		(fingerprint.gpuName ? titleCaseGpu(fingerprint.gpuName) : null);
	const device = fingerprint.model || null;
	const hash = fingerprint.hash || null;

	const parts = [os, renderer, device].filter(
		(part): part is string => !!part && part.length > 0,
	);

	return {
		label: parts.length > 0 ? parts.join(" · ") : null,
		os,
		device,
		renderer,
		hash,
	};
}

/**
 * Has this session already ended?
 *
 * The two id spaces answer differently and both answers have to be read
 * correctly, because getting this wrong means a dialog that promises to
 * destroy automation state for a browser that exited hours ago:
 *
 * - The launch registry only holds live browsers, so it 404s once the browser
 *   is gone. The proxy turns that into a `SESSION_GONE` message.
 * - The REST session store keeps the row and reports `status: "exited"`,
 *   which is richer: it still carries the fingerprint, so the dialog can say
 *   which browser it was as well as that it is over.
 *
 * A transport failure is deliberately not "gone". Not being able to ask is
 * different from having an answer, and claiming the session ended because a
 * worker timed out would be its own lie.
 */
export function isSessionGone(args: {
	status?: string | null;
	errorMessage?: string | null;
}): boolean {
	// `killed` is what the store records for a session stopped through
	// `DELETE /sessions/:id`, as opposed to `exited` for a browser that went on
	// its own. Both are terminal in local-api's schema; only `running` is not.
	if (
		args.status === "exited" ||
		args.status === "killed" ||
		args.status === "failed"
	) {
		return true;
	}
	if (!args.errorMessage) return false;
	return /\b404\b|no longer running|no record of this session/i.test(
		args.errorMessage,
	);
}

/** "4m 12s" — same vocabulary the live sessions list already uses. */
export function formatUptime(seconds: number): string {
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
	return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Enough of a uuid to tell two apart, without pretending to be a name. */
export function shortId(id: string | null | undefined): string {
	return id ? id.slice(0, 8) : "unknown";
}
